import React, { FC, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
} from "recharts";
import styles from "./charts.module.css";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip as AntdTooltip, Empty } from "antd";

const {
  ["chart-container"]: chartContainerClassName,
  ["chart-title"]: chartTitleClassName,
  ["chart-wrapper"]: chartWrapperClassName,
  ["empty-container"]: emptyContainerClassName,
} = styles;

interface TimeSeriesUsageData {
  timeLabelString: string;
  percentage: number | null;
}

interface PercentageLineChartProps {
  data: TimeSeriesUsageData[] | undefined;
  title: string;
  explanatoryText?: string;
  width?: number;
  height?: number;
  yAxisLabel?: string;
}

const PercentageLineChart: FC<PercentageLineChartProps> = ({
  data,
  title,
  explanatoryText,
  width = 600,
  height = 400,
  yAxisLabel = "Percentage",
}) => {
  const validData = useMemo(() => {
    if (!data) return [];
    return data.filter((item) => item.percentage !== null && !isNaN(item.percentage));
  }, [data]);

  const customTooltip = (props: TooltipProps<any, any>) => {
    if (props.active && props.payload && props.payload.length) {
      return (
        <div
          style={{
            backgroundColor: "white",
            padding: "10px",
            border: "1px solid #ccc",
          }}
        >
          <p>{`${props.label}`}</p>
          {props.payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.name}: ${Number(entry.value).toFixed(1)}%`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className={chartContainerClassName} style={{ width, height }}>
      <h2 className={chartTitleClassName}>
        {title}{" "}
        {explanatoryText && (
          <AntdTooltip placement="top" title={explanatoryText}>
            <InfoCircleOutlined style={{ paddingLeft: "5px" }} />
          </AntdTooltip>
        )}
      </h2>
      <div className={chartWrapperClassName}>
        {!data || data.length === 0 || validData.length === 0 ? (
          <div className={emptyContainerClassName}>
            <Empty description="No data" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={validData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="timeLabelString" />
              <YAxis label={{ value: yAxisLabel, angle: -90, position: "insideLeft" }} />
              <Tooltip content={customTooltip} />
              <Line
                key="percentage"
                type="monotone"
                dataKey="percentage"
                name="Percentage Linked"
                activeDot={{ r: 8 }}
                connectNulls={true}
                strokeWidth={2}
                animationBegin={100}
                animationDuration={800}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default PercentageLineChart;
